import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  InputAdornment,
  IconButton,
  TextField,
  makeStyles,
  Divider,
  Tooltip,
  useMediaQuery,
  useTheme
} from '@material-ui/core';
import moment from 'moment-timezone';
import {
  getDateValues,
  getTypeByRange,
  incrementOrDecrementByType,
  getFiscalConfigFromBiz,
  incrementOrDecrementFiscalYear,
  isFiscalYearValue,
  getFiscalYearValues
} from './utils';
import StyledTab from 'src/components/StyledTab';
import DateRangeIcon from '@material-ui/icons/DateRange';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faCalendarDays
} from '@fortawesome/free-regular-svg-icons';
import {
  faCalendarDay,
  faCalendarWeek,
  faTableCellsLarge
} from '@fortawesome/free-solid-svg-icons';

//Dates
import Day from './Day';
import Week from './Week';
import Month from './Month';
import Year from './Year';
import Fiscal from './Fiscal';
import Range from './Range';
import Quarterly from './Quarterly';
import { NavigateBefore, NavigateNext, Cancel } from '@material-ui/icons';
import { capitalize } from 'lodash';
import { useSelector } from 'react-redux';

const useStyles = makeStyles({
  tabs: {
    '& .MuiTab-root': {
      marginRight: 0
    },
    '& .MuiTabs-indicator': {
      display: 'none'
    }
  },
  root: {
    padding: '0px 0px',
    display: 'flex',
    alignItems: 'center',
    width: 500,
    minWith: 500
  },
  input: {
    padding: '1px',
    paddingLeft: '-2px',
    flex: 1
  },
  iconButton: {
    padding: 10,
    margin: '1px'
  },
  adornStart: {
    paddingLeft: 1
  },
  adornEnd: {
    paddingRight: 4
  },
  divider: {
    height: 28,
    margin: 2,
    marginRight: 0
  }
});

const tabList = [
  {
    id: 1,
    title: 'Day',
    type: 'day',
    icon: <FontAwesomeIcon icon={faCalendarDay} />,
    picker: Day
  },
  {
    id: 2,
    title: 'Month',
    type: 'month',
    icon: <FontAwesomeIcon icon={faCalendarDays} />,
    picker: Month
  },
  {
    id: 3,
    title: 'Week',
    type: 'week',
    icon: <FontAwesomeIcon icon={faCalendarDays} />,
    picker: Week
  },
  {
    id: 5,
    title: 'Quarter',
    type: 'quarter',
    icon: <FontAwesomeIcon icon={faTableCellsLarge} />,
    picker: Quarterly
  },
  {
    id: 4,
    title: 'Year',
    type: 'year',
    icon: <FontAwesomeIcon icon={faCalendar} />,
    picker: Year
  },
  {
    id: 6,
    title: 'Range',
    type: 'range',
    icon: <FontAwesomeIcon icon={faCalendarWeek} />,
    picker: Range
  }
];

export const getDatePickerComponent = ({ type, ...data }) => {
  return {
    day: <Day {...data} />,
    month: <Month {...data} />,
    week: <Week {...data} />,
    year: <Year {...data} />,
    fiscal: <Fiscal {...data} />,
    range: <Range {...data} />,
    quarter: <Quarterly {...data} />
  }[type];
};

const DatePicker = ({
  type = 'day', // day, month,year,quarter,range
  values = {
    dt1: null,
    dt2: null,
    textValue: '',
    lastUpdate: ''
  },
  prefixText = '', // for , as of etc
  defaultDateRangePicker = '', // range, month ,day,year
  onChange = () => null,
  disabled = false,
  datePickers = ['day', 'month', 'year', 'fiscal', 'quarter', 'range'],
  size = 'medium',
  clearable = false,
  ...props
}) => {
  const theme = useTheme();
  const onMobile = useMediaQuery(theme.breakpoints.only('xs'));
  const biz = useSelector(({ bizMeta }) => bizMeta);
  const frontEnd = biz.frontEnd || {};

  // HOOKS
  const classNames = useStyles();

  const currentType = getTypeByRange(values.dt1, values.dt2);
  let tabs = tabList.filter(item => datePickers.includes(item.type));
  const fiscalMeta = getFiscalConfigFromBiz(biz);
  const fiscalAllowed = fiscalMeta.allow;
  const yearIdx = tabs.findIndex(t => t.type === 'year');
  if (fiscalAllowed && yearIdx !== -1) {
    // only show when datepicker has a year
    const fiscalTab = {
      id: 7,
      title: 'Fiscal',
      type: 'fiscal',
      icon: <FontAwesomeIcon icon={faCalendar} />,
      picker: Fiscal
    };
    if (!tabs.some(t => t.type === 'fiscal')) {
      if (yearIdx !== -1) tabs.splice(yearIdx + 1, 0, fiscalTab);
      else tabs.push(fiscalTab);
    }
  }

  // STATES
  const [activeTab, setActiveTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [hasManuallySelectedTab, setHasManuallySelectedTab] = useState(false);

  // METHODS
  const updateActiveTab = (e, val) => {
    setActiveTab(val);
    setHasManuallySelectedTab(true);
  };

  // moment format() automatically adds timezone ->  https://momentjs.com/
  // ISO8601 format YYYY-MM-DDTHH:mm:ssZ -> https://momentjscom.readthedocs.io/en/latest/moment/04-displaying/01-format/
  const formatDatesWithTZ = values => {
    const tz = frontEnd?.tz ?? '',
      dt1 = moment(values.dt1),
      dt2 = moment(values.dt2);

    if (tz !== '') {
      dt1.tz(tz);
      dt2.tz(tz);
    }

    return {
      dt1: dt1.format(),
      dt2: dt2.format(),
      textValue: values.textValue,
      lastUpdate: moment().format()
    };
  };

  const handleChangeDatePickerValue = values => {
    onChange(formatDatesWithTZ(values));
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleSetDefaultDate = () => {
    const effectiveType = hasManuallySelectedTab
      ? tabs?.[activeTab]?.type
      : type === 'range' && defaultDateRangePicker !== ''
      ? defaultDateRangePicker
      : type;

    if (fiscalAllowed && effectiveType === 'fiscal') {
      onChange(
        formatDatesWithTZ({
          ...getFiscalYearValues({
            year: moment().year(),
            startMonth: fiscalMeta.startMonth || 1
          }),
          lastUpdate: moment().format()
        })
      );
    } else {
      const defaultValues = getDateValues[effectiveType]({
        values: {
          dt1: moment().format('YYYY-MM-DD'),
          dt2: moment().format('YYYY-MM-DD')
        },
        prefixText: prefixText === '' ? effectiveType : prefixText
      });
      onChange(formatDatesWithTZ(defaultValues));
    }
  };

  const getDatePicker = props => {
    const Picker = tabs?.[activeTab]?.picker ?? Day;
    const startMonth = fiscalMeta.startMonth;
    const endMonth = fiscalMeta.endMonth;
    return (
      <Picker
        {...props}
        fiscal={fiscalAllowed}
        fiscalStartMonth={Number(startMonth) || 0}
        fiscalEndMonth={Number(endMonth) || 0}
      />
    );
  };

  function next(event) {
    event.stopPropagation();
    const navigationType = hasManuallySelectedTab
      ? tabs?.[activeTab]?.type
      : currentType;
    if (
      fiscalAllowed &&
      (navigationType === 'fiscal' || isFiscalYearValue(values))
    ) {
      onChange(
        formatDatesWithTZ(
          incrementOrDecrementFiscalYear(
            values,
            fiscalMeta.startMonth || 1,
            'increment'
          )
        )
      );
      return;
    }
    onChange(
      formatDatesWithTZ(
        incrementOrDecrementByType(values, navigationType, 'increment')
      )
    );
  }

  function prev(event) {
    event.stopPropagation();
    const navigationType = hasManuallySelectedTab
      ? tabs?.[activeTab]?.type
      : currentType;
    if (
      fiscalAllowed &&
      (navigationType === 'fiscal' || isFiscalYearValue(values))
    ) {
      onChange(
        formatDatesWithTZ(
          incrementOrDecrementFiscalYear(
            values,
            fiscalMeta.startMonth || 1,
            'decrement'
          )
        )
      );
      return;
    }
    onChange(
      formatDatesWithTZ(
        incrementOrDecrementByType(values, navigationType, 'decrement')
      )
    );
  }

  // USE_EFFECTS
  useEffect(() => {
    if (type === 'range' && defaultDateRangePicker !== '')
      type = defaultDateRangePicker;

    // Only auto-set active tab if user hasn't manually selected one
    if (!hasManuallySelectedTab) {
      const index = tabs.findIndex(item => item.type === type);
      setActiveTab(index !== -1 ? index : 0);
    }

    if (
      ((!values.dt1 || !values.dt2) && !clearable) ||
      (values.lastUpdate && moment().diff(values.lastUpdate, 'days') >= 7)
    ) {
      if (fiscalAllowed && type === 'fiscal') {
        onChange(
          formatDatesWithTZ({
            ...getFiscalYearValues({
              year: moment().year(),
              startMonth: fiscalMeta.startMonth || 1
            }),
            lastUpdate: moment().format()
          })
        );
      } else {
        const validTypes = Object.keys(getDateValues);
        const isValidType =
          typeof type === 'string' && validTypes.includes(type);
        const safeType = isValidType ? type : 'month';

        const defaultValues = getDateValues[safeType]({
          values: {
            dt1: moment().format('YYYY-MM-DD'),
            dt2: moment().format('YYYY-MM-DD')
          },
          prefixText: prefixText === '' ? type : prefixText
        });
        onChange(formatDatesWithTZ(defaultValues));
      }
    }
  }, [
    defaultDateRangePicker,
    values,
    type,
    values.lastUpdate,
    hasManuallySelectedTab
  ]);

  return (
    <Box>
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth
        maxWidth={tabs?.[activeTab]?.id == 3 ? 'md' : 'xs'}
        fullScreen={onMobile}
      >
        <StyledTab
          tabs={tabs}
          activeTab={activeTab}
          onChange={updateActiveTab}
          className={classNames.tabs}
        />
        <Divider />
        <Box>
          {getDatePicker({
            handleChangeDatePickerValue,
            handleCloseDialog,
            dateValues: values,
            defaultDateRangePicker,
            defaultDatePicker: type,
            prefixText
          })}
        </Box>
      </Dialog>

      <TextField
        className={classNames.input}
        fullWidth
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <IconButton
                aria-label="search"
                className={classNames.iconButton}
                onClick={handleOpenDialog}
                disabled={disabled}
                size={size}
              >
                <DateRangeIcon />
              </IconButton>
            </InputAdornment>
          ),
          endAdornment: (
            <>
              {clearable && (
                <InputAdornment position="end">
                  <Tooltip
                    title={
                      !values.dt1 || !values.dt2 ? 'Set Default Date' : 'Clear'
                    }
                  >
                    <IconButton
                      aria-label={
                        !values.dt1 || !values.dt2 ? 'set-default' : 'clear'
                      }
                      size="small"
                      onClick={e => {
                        e.stopPropagation();
                        if (!values.dt1 || !values.dt2) {
                          handleSetDefaultDate();
                        } else {
                          onChange({
                            dt1: null,
                            dt2: null,
                            textValue: ''
                          });
                        }
                      }}
                      disabled={disabled}
                    >
                      {!values.dt1 || !values.dt2 ? (
                        <DateRangeIcon color="primary" />
                      ) : (
                        <Cancel color="primary" />
                      )}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )}

              <InputAdornment position="end">
                <Tooltip title={'Prev ' + capitalize(currentType)}>
                  <span>
                    <IconButton
                      onClick={prev}
                      size="small"
                      disabled={disabled || !currentType}
                      color="primary"
                    >
                      <NavigateBefore />
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
              <InputAdornment position="start">
                <Tooltip title={'Next ' + capitalize(currentType)}>
                  <span>
                    <IconButton
                      onClick={next}
                      size="small"
                      disabled={disabled || !currentType}
                      color="primary"
                    >
                      <NavigateNext />
                    </IconButton>
                  </span>
                </Tooltip>
              </InputAdornment>
            </>
          )
        }}
        onClick={handleOpenDialog}
        value={values?.textValue || ''}
        placeholder="Please set a date"
        variant="outlined"
        disabled={disabled}
        size={size}
        {...props}
      />
    </Box>
  );
};

export { DatePicker };
export default DatePicker;
